/**
 * TutorialService — the in-game tutorial controller.
 *
 * The tutorial is a fully SCRIPTED guide (no server, no real match): it
 * walks the player through 11 tips in a fixed order, each tip a small
 * dialogue that points at and highlights the element the user must use.
 * The user may only perform the CURRENT tip's action (the TutorialShell —
 * the tutorial's "authoritative backend" — rejects everything else), and
 * every tip has a "Next" button which is enabled only once the tip's
 * action has been performed (info tips need no action).
 *
 * Reuse: the tutorial does NOT build its own board — it drives the REAL
 * match-screen DOM through the real components (HandUI, BattlefieldUI,
 * MatchHUD, …) bound to a TutorialShell, so every future card/HUD change
 * applies to the tutorial automatically. This service only owns:
 *  - the tip script (TUTORIAL_STEPS — append-safe, data-driven),
 *  - the step state machine (completion detection from state diffs),
 *  - the dialogue + spotlight highlight UI (#tutorial-* elements),
 *  - the per-step gold grants and the scripted second minion for the
 *    reposition tip.
 */

import { TutorialShell, TutorialAction } from './TutorialShell';
import { CardInspectService } from './CardInspectService';
import { GameConfig } from '../../config/gameConfig';
import { RUNE_POWER_ID, getRuneById } from '../../shared/models/RuneDefinitions';

// ─── Tip script ────────────────────────────────────────────────────────────

/** What the dialogue points at / spotlights (null = centered info dialog). */
export type TutorialTarget =
  | { kind: 'selector'; selector: string }
  | { kind: 'first-battlefield-minion' }
  | { kind: 'sell-zone' };

/**
 * One tutorial tip. The array is the single source for the tour — the tips
 * MUST be completed in array order, and the user may only perform the
 * current tip's action (enforced by the TutorialShell's gating).
 */
export interface TutorialStepDef {
  /** Stable id (tests + debugging). */
  id: string;
  /** Dialogue title. */
  title: string;
  /** Dialogue body. */
  body: string;
  /** Highlighted target (null = no spotlight, dialog centered). */
  target: TutorialTarget | null;
  /**
   * The action the user must perform to unlock "Next" (null = pure info
   * tip — Next is enabled immediately).
   */
  action: TutorialAction | null;
  /** Gold the player is topped up to when this tip becomes active. */
  grantGold?: number;
}

/** The "Next" button label (spec: every tip carries a button saying "Next"). */
export const TUTORIAL_NEXT_BUTTON_LABEL = 'Next';

/**
 * The tutorial script, in the exact order the tips must be completed:
 * play → upgrade → reposition → timer info → sell → buy → rune →
 * long-press → ready → gold info → end.
 */
export const TUTORIAL_STEPS: readonly TutorialStepDef[] = [
  {
    id: 'play',
    title: 'Play your minions',
    body:
      'Drag ALL of your cards from your hand down onto the battlefield to play them. ' +
      'Your minions fight for you in combat — their attack and health decide the fight.',
    target: { kind: 'selector', selector: '#hand' },
    action: 'play',
  },
  {
    id: 'upgrade',
    title: 'Upgrade a minion',
    body:
      `We gave you ${GameConfig.upgradeCost} gold. Double-click a minion on your battlefield to ` +
      'open the upgrade menu, then pick a boost to apply it.',
    target: { kind: 'first-battlefield-minion' },
    action: 'upgrade',
    grantGold: GameConfig.upgradeCost,
  },
  {
    id: 'reposition',
    title: 'Change the order',
    body:
      'Drag a minion on your battlefield to move it. The leftmost minion attacks ' +
      'first in combat, so the order of your board matters!',
    target: { kind: 'first-battlefield-minion' },
    action: 'reposition',
  },
  {
    id: 'timer',
    title: 'The preparation timer',
    body:
      'This is the round timer. In a real game it counts down your time to set up ' +
      'the board — when it hits zero, combat starts automatically. In this tutorial ' +
      'it is frozen at 42 seconds, so you have all the time in the world.',
    target: { kind: 'selector', selector: '#hud-timer' },
    action: null,
  },
  {
    id: 'sell',
    title: 'Sell a card',
    body:
      'Drag a card — from your hand or from your battlefield — up into the sell ' +
      'zone to sell it. You get gold back equal to its level (a Lv.1 card pays 1g).',
    target: { kind: 'sell-zone' },
    action: 'sell',
  },
  {
    id: 'buy',
    title: 'Buy a card',
    body:
      `We gave you ${GameConfig.cardPurchaseCost} gold. Click the card button to buy a ` +
      `new random card into your hand for ${GameConfig.cardPurchaseCost}g.`,
    target: { kind: 'selector', selector: '#buy-card-btn' },
    action: 'buy',
    grantGold: GameConfig.cardPurchaseCost,
  },
  {
    id: 'rune',
    title: 'Buy a rune',
    body:
      `We gave you ${getRuneById(RUNE_POWER_ID)?.goldCost ?? 10} gold. Click the rune button to open ` +
      `the rune shop, then buy the Rune of Power for ${getRuneById(RUNE_POWER_ID)?.goldCost ?? 10}g: ` +
      'all your minions get +3 ATK during combat, for the rest of the game. ' +
      'In real matches the shop lists every rune — each can be bought once, at its gold price.',
    target: { kind: 'selector', selector: '#buy-rune-btn' },
    action: 'rune',
    grantGold: getRuneById(RUNE_POWER_ID)?.goldCost ?? 10,
  },
  {
    id: 'longpress',
    title: 'Read a card in detail',
    body:
      'Press and hold a card (about half a second, touch or mouse) to see a large ' +
      'preview with its full ability text.',
    target: { kind: 'first-battlefield-minion' },
    action: 'longpress',
  },
  {
    id: 'ready',
    title: 'Mark yourself ready',
    body:
      'When your board is set, press the Ready button. In a real game, combat ' +
      'starts as soon as every player is ready.',
    target: { kind: 'selector', selector: '#ready-btn' },
    action: 'ready',
  },
  {
    id: 'gold',
    title: 'How gold works',
    body:
      'Gold is your only currency. You earn it from kills in combat (the victim’s ' +
      'level in gold), from selling minions (its level in gold) and as a small ' +
      'consolation after losses. You spend it on new cards, on upgrades and on runes.',
    target: null,
    action: null,
  },
  {
    id: 'end',
    title: 'You’re all set!',
    body:
      'You know the basics: play minions from your hand, upgrade and order your ' +
      'board, buy cards and runes, and hit Ready before the timer runs out. Join ' +
      'the queue when you are ready — good luck!',
    target: null,
    action: null,
  },
];

// ─── Service options / state snapshot ──────────────────────────────────────

export interface TutorialServiceOptions {
  /** The scripted tutorial "server" the tips are bound to. */
  shell: TutorialShell;
  /** The long-press preview service (for the long-press tip). */
  cardInspect: CardInspectService;
  /** Fired when the tour finishes (last "Next" or Exit). */
  onFinish: () => void;
}

/** The player-side facts a tip watches for (the completion signal). */
interface StepSnapshot {
  handIds: string[];
  boardIds: string[];
  boardUpgradeCounts: number[];
  purchasedRunes: string[];
  readyIds: string[];
}

// ─── TutorialService ───────────────────────────────────────────────────────

export class TutorialService {
  private readonly _shell: TutorialShell;
  private readonly _cardInspect: CardInspectService;
  private readonly _onFinish: () => void;

  private _stepIndex = 0;
  private _active = false;
  private _stepDone = false;
  private _longPressFired = false;
  private _prevSnapshot: StepSnapshot | null = null;
  /** True while the dialog is minimized to the corner chip. */
  private _minimized = false;

  // Dialog drag (mouse + touch via pointer events): the user can move the
  // tip window anywhere on screen; while pinned, the 100 ms layout tick
  // stops auto-repositioning it (a new tip re-pins to the auto spot).
  private _dragging = false;
  private _userPinned = false;
  private _dragStart = { px: 0, py: 0, x: 0, y: 0 };
  /** A drag that just ended: the synthetic click it produces must not be
   *  read as "click outside → minimize". */
  private _dragEndAt = 0;

  // Minimize/expand animation (the dialog visibly shrinks into the corner
  // chip and back). WAAPI-based, skipped under prefers-reduced-motion and
  // in engines without element.animate (jsdom).
  private _minimizeAnim: Animation | null = null;

  // AUTO-PROCEED (playtest: "no use having to click Next"): when an ACTION
  // tip completes, the tour advances itself after a short beat (the
  // completion FX + "✓ Nice!" get a moment to register). The Next button
  // stays as the manual fast-forward. INFO tips (action === null) NEVER
  // auto-advance — the user has to read them (and click Next).
  private _autoAdvanceTimer: ReturnType<typeof setTimeout> | null = null;

  private _layoutTimer: ReturnType<typeof setInterval> | null = null;
  private _stateUnsubscribe: (() => void) | null = null;
  private _resizeHandler: (() => void) | null = null;

  // DOM (queried in start() — the elements are static in index.html)
  private _overlay!: HTMLElement;
  private _highlight!: HTMLElement;
  private _dialog!: HTMLElement;
  private _stepLabel!: HTMLElement;
  private _title!: HTMLElement;
  private _body!: HTMLElement;
  private _nextBtn!: HTMLButtonElement;
  private _exitBtn!: HTMLButtonElement;
  private _quitBtn!: HTMLButtonElement;
  private _doneNote!: HTMLElement;
  private _chip!: HTMLElement;
  private _quitConfirm!: HTMLElement;
  private _quitConfirmCancel!: HTMLButtonElement;
  private _quitConfirmQuit!: HTMLButtonElement;
  private _escapeHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(options: TutorialServiceOptions) {
    this._shell = options.shell;
    this._cardInspect = options.cardInspect;
    this._onFinish = options.onFinish;
  }

  // ─── Accessors ───────────────────────────────────────────────────────────

  get isActive(): boolean {
    return this._active;
  }

  /** 0-based index of the current tip. */
  get stepIndex(): number {
    return this._stepIndex;
  }

  /** The current tip definition. */
  get currentStep(): TutorialStepDef {
    return TUTORIAL_STEPS[this._stepIndex];
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /** Starts the tour at tip 1 (call once; the match screen must be visible). */
  start(): void {
    if (this._active) return;
    this._active = true;

    this._overlay = document.getElementById('tutorial-overlay') as HTMLElement;
    this._highlight = document.getElementById('tutorial-highlight') as HTMLElement;
    this._dialog = document.getElementById('tutorial-dialog') as HTMLElement;
    this._stepLabel = document.getElementById('tutorial-step-label') as HTMLElement;
    this._title = document.getElementById('tutorial-title') as HTMLElement;
    this._body = document.getElementById('tutorial-body') as HTMLElement;
    this._nextBtn = document.getElementById('tutorial-next-btn') as HTMLButtonElement;
    this._exitBtn = document.getElementById('tutorial-exit-btn') as HTMLButtonElement;
    this._quitBtn = document.getElementById('tutorial-quit-btn') as HTMLButtonElement;
    this._doneNote = document.getElementById('tutorial-done-note') as HTMLElement;

    this._chip = document.getElementById('tutorial-chip') as HTMLElement;
    this._quitConfirm = document.getElementById('tutorial-quit-confirm') as HTMLElement;
    this._quitConfirmCancel = document.getElementById('tutorial-quit-confirm-cancel') as HTMLButtonElement;
    this._quitConfirmQuit = document.getElementById('tutorial-quit-confirm-quit') as HTMLButtonElement;

    this._nextBtn.textContent = TUTORIAL_NEXT_BUTTON_LABEL;
    this._nextBtn.addEventListener('click', () => this.advance());
    // The dialog's ✕ MINIMIZES the tip (into the corner chip) — it does
    // NOT quit the tutorial. Quitting is the floating #tutorial-quit-btn,
    // which asks for confirmation first.
    this._exitBtn.addEventListener('click', () => this._setMinimized(true));
    this._quitBtn.addEventListener('click', () => this.requestQuit());
    this._quitConfirmCancel.addEventListener('click', () => this._hideQuitConfirm());
    this._quitConfirmQuit.addEventListener('click', () => {
      this._hideQuitConfirm();
      this.finish();
    });
    // Escape closes the confirmation (the same affordance the native
    // dialog it replaces had).
    this._escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !this._quitConfirm.classList.contains('hidden')) {
        this._hideQuitConfirm();
      }
    };
    document.addEventListener('keydown', this._escapeHandler);
    this._chip.addEventListener('click', () => this._setMinimized(false));

    // Dialog drag (pointer events — one path for mouse AND touch).
    this._dialog.addEventListener('pointerdown', this._onDialogPointerDown);
    window.addEventListener('pointermove', this._onDialogPointerMove);
    window.addEventListener('pointerup', this._onDialogPointerUp);
    window.addEventListener('pointercancel', this._onDialogPointerUp);

    // Click ANYWHERE else (on the game UI) minimizes the dialog into the
    // pulsing corner chip — the tip must never block the interaction it
    // teaches. Clicks inside the dialog itself (Next / Exit / chip) are
    // exempt. Captured (not bubbled) so clicks that get stopPropagation()
    // in their own handler (e.g. the card's Play button) still minimize.
    // A click that COMPLETES the tip minimizes first, then the synchronous
    // completion re-expands the dialog to the "✓ Nice! Press Next" state —
    // the net result of a completing click is always the expanded dialog.
    document.addEventListener('click', this._onDocumentClick, true);

    // The long-press tip: the preview service fires onInspect exactly when
    // the large preview appears (a real long press completed).
    this._cardInspect.setOnInspect(() => {
      if (this.currentStep.action === 'longpress') {
        this._longPressFired = true;
        this._checkCompletion();
      }
    });

    // Completion detection: watch the tutorial player's state (the shell
    // is authoritative — what it broadcasts is what happened).
    this._stateUnsubscribe = this._shell.onEvent((event) => {
      if (event.type === 'stateChange') this._onStateChange();
    });

    // Keep the spotlight + dialog pinned to their targets while the tour
    // runs (cards re-render on every state change, the viewport can resize).
    this._layoutTimer = setInterval(() => this._layout(), 100);
    this._resizeHandler = () => this._layout();
    window.addEventListener('resize', this._resizeHandler);

    this._activateStep();
  }

  /** Tears the tour down (dialog, highlight, listeners). Call once. */
  destroy(): void {
    if (!this._active) return;
    this._active = false;

    if (this._layoutTimer !== null) {
      clearInterval(this._layoutTimer);
      this._layoutTimer = null;
    }
    if (this._stateUnsubscribe) {
      this._stateUnsubscribe();
      this._stateUnsubscribe = null;
    }
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }
    document.removeEventListener('click', this._onDocumentClick, true);
    this._dialog.removeEventListener('pointerdown', this._onDialogPointerDown);
    window.removeEventListener('pointermove', this._onDialogPointerMove);
    window.removeEventListener('pointerup', this._onDialogPointerUp);
    window.removeEventListener('pointercancel', this._onDialogPointerUp);
    this._cardInspect.setOnInspect(null);
    this._shell.setAllowedActions([]);
    if (this._escapeHandler) {
      document.removeEventListener('keydown', this._escapeHandler);
      this._escapeHandler = null;
    }

    this._clearAutoAdvance();
    this._cancelMinimizeAnim();
    this._quitConfirm.classList.add('hidden');
    this._overlay.classList.add('hidden');
    this._highlight.style.display = 'none';
    this._chip.classList.add('hidden');
  }

  // ─── Quit (floating button, with confirmation) ─────────────────────────

  /**
   * The floating "Quit" button (always visible, upper-right): opens the
   * in-app confirmation POPOVER (not window.confirm — the native dialog
   * froze the page and read as out-of-app; 2026-09-29 playtest). The tour
   * finishes only on the popover's "Quit"; "Cancel" / Escape / an outside
   * click keep it running.
   */
  requestQuit(): void {
    if (!this._active) return;
    this._quitConfirm.classList.remove('hidden');
  }

  /** Closes the quit confirmation popover (Cancel / Escape / outside). */
  private _hideQuitConfirm(): void {
    this._quitConfirm.classList.add('hidden');
  }

  // ─── Minimize / expand ─────────────────────────────────────────────────

  // ─── Dialog drag (mouse + touch via pointer events) ─────────────────────

  /**
   * Pointerdown on the dialog (NOT on a button) starts a drag. The
   * pointermove/pointerup listeners live on window (registered once in
   * start()) so the drag keeps tracking when the pointer leaves the
   * dialog. Works for mouse AND touch (pointer events + touch-action:
   * none on the dialog in the CSS).
   */
  private _onDialogPointerDown = (e: PointerEvent): void => {
    if (!this._active || this._minimized) return;
    // Primary button only for mouse (touch/pen report button 0 by default).
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const t = e.target as HTMLElement | null;
    if (t && t.closest('button')) return; // Next/✕ keep their click behavior
    e.preventDefault(); // no text selection / native drag
    this._dragging = true;
    // The user placed the dialog: the 100 ms layout tick must stop
    // auto-repositioning it (until the next tip).
    this._userPinned = true;
    const rect = this._dialog.getBoundingClientRect();
    this._dragStart = { px: e.clientX, py: e.clientY, x: rect.left, y: rect.top };
    this._dialog.classList.add('tutorial-dragging');
  };

  private _onDialogPointerMove = (e: PointerEvent): void => {
    if (!this._dragging) return;
    const w = this._dialog.offsetWidth || 320;
    const h = this._dialog.offsetHeight || 140;
    const margin = 8;
    const x = Math.max(margin, Math.min(
      this._dragStart.x + (e.clientX - this._dragStart.px),
      window.innerWidth - w - margin,
    ));
    const y = Math.max(margin, Math.min(
      this._dragStart.y + (e.clientY - this._dragStart.py),
      window.innerHeight - h - margin,
    ));
    this._dialog.style.left = `${x}px`;
    this._dialog.style.top = `${y}px`;
  };

  private _onDialogPointerUp = (): void => {
    if (!this._dragging) return;
    this._dragging = false;
    // The synthetic click a drag produces (released outside the dialog)
    // must not be read as "click outside → minimize".
    this._dragEndAt = Date.now();
    this._dialog.classList.remove('tutorial-dragging');
  };

  private _onDocumentClick = (e: Event): void => {
    if (!this._active || this._minimized) return;
    // A click that is really the tail of a just-finished dialog drag.
    if (Date.now() - this._dragEndAt < 300) return;
    const t = e.target as HTMLElement | null;
    if (
      t &&
      (this._dialog.contains(t) ||
        this._chip.contains(t) ||
        this._quitBtn.contains(t) ||
        this._quitConfirm.contains(t))
    ) {
      return; // the quit button + its confirmation decide themselves
    }
    // A click OUTSIDE the open confirmation dismisses it (popover rule).
    if (!this._quitConfirm.classList.contains('hidden')) this._hideQuitConfirm();
    this._setMinimized(true);
  };

  private _setMinimized(minimized: boolean): void {
    if (this._minimized === minimized) return;
    this._minimized = minimized;
    // A pending animation can't survive a direction flip — cancel it and
    // render the final state immediately.
    this._cancelMinimizeAnim();
    // When the animation plays, the hidden classes land in its onfinish.
    if (!this._playMinimizeAnimation(minimized)) {
      this._renderDialog();
    }
  }

  /**
   * Plays the minimize/expand animation (the dialog visibly shrinks into
   * the corner chip / grows back out of it, so the user sees WHERE the
   * tip went). Returns true when the animation was started (the hidden
   * classes are applied in its onfinish), false when skipped (reduced
   * motion / no WAAPI — the caller renders the final state immediately).
   */
  /** The chip's viewport center — measured while the chip is VISIBLE (a
   *  display:none chip has a zero rect and the animation would fly to the
   *  top-left (0,0) corner instead of the chip's bottom-left spot — the
   *  2026-09-29 playtest "minimizes to the wrong corner" bug). */
  private _chipCenter(): { x: number; y: number } {
    const r = this._chip.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  private _playMinimizeAnimation(minimized: boolean): boolean {
    if (this._reducedMotion()) return false;
    if (typeof this._dialog.animate !== 'function') return false;

    if (minimized) {
      // The chip is hidden right now — reveal it FIRST so its real
      // bottom-left position is measurable; the dialog shrinks INTO it
      // (and it stays visible from here on: that IS the minimized state).
      this._chip.classList.remove('hidden');
      const chip = this._chipCenter();
      const d = this._dialog.getBoundingClientRect();
      const dx = chip.x - (d.left + d.width / 2);
      const dy = chip.y - (d.top + d.height / 2);
      const anim = this._dialog.animate(
        [
          { transform: 'translate(0px, 0px) scale(1)', opacity: '1' },
          { transform: `translate(${dx}px, ${dy}px) scale(0.08)`, opacity: '0' },
        ],
        { duration: 200, easing: 'cubic-bezier(0.55, 0, 1, 0.45)' },
      );
      this._minimizeAnim = anim;
      anim.onfinish = () => {
        this._minimizeAnim = null;
        this._renderDialog(); // dialog hidden, chip shown
      };
      return true;
    }

    // Expand: capture the chip's center WHILE IT IS STILL VISIBLE (the
    // render below hides it — a hidden chip has a zero rect), then unhide
    // + place the dialog and grow it back out of the chip.
    const chip = this._chipCenter();
    this._renderDialog(); // dialog visible, chip hidden, content fresh
    this._layout();
    const d = this._dialog.getBoundingClientRect();
    const dx = chip.x - (d.left + d.width / 2);
    const dy = chip.y - (d.top + d.height / 2);
    const anim = this._dialog.animate(
      [
        { transform: `translate(${dx}px, ${dy}px) scale(0.08)`, opacity: '0' },
        { transform: 'translate(0px, 0px) scale(1)', opacity: '1' },
      ],
      { duration: 220, easing: 'cubic-bezier(0, 0.45, 0.45, 1)' },
    );
    this._minimizeAnim = anim;
    anim.onfinish = () => { this._minimizeAnim = null; };
    return true;
  }

  private _cancelMinimizeAnim(): void {
    if (this._minimizeAnim) {
      this._minimizeAnim.cancel();
      this._minimizeAnim = null;
    }
  }

  /** prefers-reduced-motion (guarded — engines may lack matchMedia). */
  private _reducedMotion(): boolean {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  }

  // ─── Step machine ────────────────────────────────────────────────────────

  /**
   * Advances to the next tip (the "Next" button). Refuses while the current
   * action tip is not done (the button is disabled — this is the backstop).
   */
  advance(): void {
    if (!this._active) return;
    const step = this.currentStep;
    if (step.action !== null && !this._stepDone) return;
    // A manual advance voids any pending auto-proceed (it would double-
    // advance the NEW tip).
    this._clearAutoAdvance();

    if (this._stepIndex + 1 >= TUTORIAL_STEPS.length) {
      this.finish();
      return;
    }
    this._stepIndex += 1;
    this._activateStep();
  }

  /** Finishes the tour (last "Next" or Exit) — main.ts returns to the menu. */
  finish(): void {
    if (!this._active) return;
    this._onFinish();
  }

  /**
   * Makes the current tip live: applies its scripted side effects (gold
   * grant, the reposition tip's second minion), sets the shell's action
   * gating, resets the completion state and renders the dialog.
   */
  private _activateStep(): void {
    const step = this.currentStep;

    // Scripted side effects of entering the tip.
    if (step.grantGold !== undefined) {
      this._shell.topUpGold(step.grantGold);
    }
    // The reposition tip needs at least TWO minions to reposition — play a
    // second one by script when the player only played one in tip 1.
    if (step.id === 'reposition') {
      const p = this._shell.getPlayer();
      if (p.boardIds.length < 2 && p.handIds.length > 0) {
        this._shell.scriptedPlayCard(p.handIds[0]);
      }
    }

    // Gating: only the current tip's action is allowed (the shell rejects
    // every other intention — the "only the current tip's action" rule).
    this._shell.setAllowedActions(step.action ? [step.action] : []);

    // Completion state: info tips are done from the start. A new tip
    // always starts with the dialog EXPANDED (minimize is the user's
    // choice, never a leftover from the previous tip) and AUTO-PLACED
    // (a user-drag position from the previous tip does not carry over).
    this._stepDone = step.action === null;
    this._longPressFired = false;
    this._minimized = false;
    this._userPinned = false;
    this._prevSnapshot = this._snapshot();
    // A pending auto-proceed from the PREVIOUS tip is stale (this tip may
    // be an info tip, or the user may have fast-forwarded manually).
    this._clearAutoAdvance();

    this._renderDialog();
    this._layout();
    // A new task starts: the tip text visibly ENLARGES (a scale pop) so
    // the moment is unmistakable (playtest).
    this._playTipPop();
  }

  /** Clears a pending auto-proceed (new tip / destroy / manual advance). */
  private _clearAutoAdvance(): void {
    if (this._autoAdvanceTimer !== null) {
      clearTimeout(this._autoAdvanceTimer);
      this._autoAdvanceTimer = null;
    }
  }

  /**
   * The new-tip "pop": the dialog (and its enlarged text) scales up and
   * settles, so a new task starts with the text visibly enlarged.
   * Skipped under prefers-reduced-motion and in engines without
   * element.animate (jsdom).
   */
  private _playTipPop(): void {
    if (this._reducedMotion()) return;
    if (typeof this._dialog.animate !== 'function') return;
    this._dialog.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.06)', offset: 0.6 },
        { transform: 'scale(1)' },
      ],
      { duration: 380, easing: 'ease-out' },
    );
  }

  /**
   * Checks whether the current tip's action has been performed, comparing
   * the new state snapshot to the one from when the tip became active.
   */
  private _onStateChange(): void {
    if (!this._active) return;
    this._checkCompletion();
    // Refresh the HUD-adjacent spotlight layout (the board may have
    // re-rendered and moved).
    this._layout();
  }

  private _checkCompletion(): void {
    const step = this.currentStep;
    if (step.action === null || this._stepDone) return;

    const prev = this._prevSnapshot;
    const now = this._snapshot();
    let done = false;
    if (prev) {
      switch (step.action) {
        case 'play':
          // The tip plays out the WHOLE starting hand — every card is on
          // the battlefield (the board only ever grows in this tip, so an
          // empty hand means all of it was played).
          done = now.handIds.length === 0;
          break;
        case 'upgrade':
          // Some minion's level went up (the board is not repositioned in
          // this tip, so the per-index comparison is stable).
          done = now.boardUpgradeCounts.some(
            (count, i) => count > (prev.boardUpgradeCounts[i] ?? 0)
          );
          break;
        case 'reposition':
          // Same set of minions in a different order.
          done = this._sameSet(prev.boardIds, now.boardIds)
            && !this._sameOrder(prev.boardIds, now.boardIds);
          break;
        case 'sell':
          // A card left the game (hand OR battlefield).
          done = now.handIds.length + now.boardIds.length
            < prev.handIds.length + prev.boardIds.length;
          break;
        case 'buy':
          done = now.handIds.length > prev.handIds.length;
          break;
        case 'rune':
          done = now.purchasedRunes.length > prev.purchasedRunes.length;
          break;
        case 'ready':
          done = now.readyIds.includes(this._shell.playerId);
          break;
        case 'longpress':
          done = this._longPressFired;
          break;
      }
    }

    if (done) {
      this._stepDone = true;
      // Auto-EXPAND on completion: the dialog pops back (even if the user
      // minimized it while doing the action) so the "✓ Nice!" moment is
      // never missed. This also resolves a completing click (capture-phase
      // minimize → synchronous completion → expand) to the expanded dialog.
      this._minimized = false;
      this._renderDialog();
      // AUTO-PROCEED: action tips advance themselves after a beat — no
      // Next click needed (the button stays as the manual fast-forward).
      // Info tips never reach here with a pending advance (stepDone is set
      // at activation, so `done` only ever fires for action tips).
      if (step.action !== null) {
        this._clearAutoAdvance();
        this._autoAdvanceTimer = setTimeout(() => {
          this._autoAdvanceTimer = null;
          // Guards: the tour may have ended (Quit) or the user may have
          // fast-forwarded manually (a new tip reset _stepDone).
          if (this._active && this._stepDone && this.currentStep.action !== null) {
            this.advance();
          }
        }, 1000);
      }
    }
  }

  private _snapshot(): StepSnapshot {
    const p = this._shell.getPlayer();
    return {
      handIds: p.handIds,
      boardIds: p.boardIds,
      boardUpgradeCounts: p.boardUpgradeCounts,
      purchasedRunes: p.purchasedRunes,
      readyIds: this._shell.gameState.match.readyPlayerIds,
    };
  }

  // ─── Dialogue + spotlight rendering ──────────────────────────────────────

  private _renderDialog(): void {
    const step = this.currentStep;
    this._overlay.classList.remove('hidden');
    // Dialog ⇄ minimized chip (the chip is the dialog's stand-in: exactly
    // one of the two is visible while the tour runs).
    this._dialog.classList.toggle('hidden', this._minimized);
    this._chip.classList.toggle('hidden', !this._minimized);
    this._stepLabel.textContent = `Tip ${this._stepIndex + 1} of ${TUTORIAL_STEPS.length}`;
    this._title.textContent = step.title;
    this._body.textContent = step.body;
    this._nextBtn.disabled = step.action !== null && !this._stepDone;
    this._doneNote.textContent =
      step.action === null ? '' : this._stepDone ? '✓ Nice! Press Next to continue.' : '';
  }

  /**
   * Resolves the current tip's target to a viewport rect (null = no
   * spotlight). The sell zone is the virtual region ABOVE the battlefield
   * (the same area the real drag-and-drop sell zone highlights).
   */
  private _targetRect(): { left: number; top: number; width: number; height: number } | null {
    const target = this.currentStep.target;
    if (!target) return null;

    if (target.kind === 'sell-zone') {
      const battlefield = document.getElementById('battlefield');
      const bottom = battlefield
        ? Math.max(0, battlefield.getBoundingClientRect().top)
        : window.innerHeight / 2;
      if (bottom <= 0) return null;
      return { left: 0, top: 0, width: window.innerWidth, height: bottom };
    }

    const selector =
      target.kind === 'first-battlefield-minion'
        ? '#battlefield .battlefield-minion'
        : target.selector;
    const el = document.querySelector(selector) as HTMLElement | null;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
  }

  /**
   * Positions the spotlight ring around the target and the dialog next to
   * it (centered when the tip has no target). Called on step change and on
   * a 100 ms interval while the tour runs (cheap DOM writes, self-guarded).
   */
  private _layout(): void {
    if (!this._active) return;
    const step = this.currentStep;
    const rect = this._targetRect();

    // Spotlight ring. ACTION tips (the user must interact with the game —
    // play, upgrade, reposition, sell, buy, …) run WITHOUT the dimming
    // veil: only the glowing ring points at the target, so the board,
    // the upgrade menu and the drop zones stay fully visible and usable.
    // The classic "dim everything around the hole" spotlight is reserved
    // for the info tips (timer/gold/end), which need no interaction —
    // when an info tip has no target rect (the centered tips) the whole
    // screen is veiled instead (no hole).
    const veilShadow = '0 0 0 9999px rgba(0, 0, 0, 0.55), 0 0 18px rgba(241, 196, 15, 0.9)';
    const glowShadow = '0 0 18px rgba(241, 196, 15, 0.9)';

    if (rect) {
      const pad = 6;
      const left = Math.max(2, Math.min(rect.left - pad, window.innerWidth - 10));
      const top = Math.max(2, Math.min(rect.top - pad, window.innerHeight - 10));
      const width = Math.max(10, rect.width + pad * 2);
      const height = Math.max(10, rect.height + pad * 2);
      this._highlight.style.display = 'block';
      this._highlight.style.background = 'transparent';
      this._highlight.style.border = '3px solid #f1c40f';
      this._highlight.style.boxShadow = step.action === null ? veilShadow : glowShadow;
      this._highlight.style.left = `${left}px`;
      this._highlight.style.top = `${top}px`;
      this._highlight.style.width = `${width}px`;
      this._highlight.style.height = `${height}px`;
    } else if (step.action === null) {
      // Info tip without a resolvable target: veil the whole screen with a
      // plain background (a box-shadow would only paint OUTSIDE the
      // element, which for a full-viewport box is off-screen) — the dialog
      // is centered on top of it.
      this._highlight.style.display = 'block';
      this._highlight.style.background = 'rgba(0, 0, 0, 0.55)';
      this._highlight.style.border = 'none';
      this._highlight.style.boxShadow = 'none';
      this._highlight.style.left = '0px';
      this._highlight.style.top = '0px';
      this._highlight.style.width = `${window.innerWidth}px`;
      this._highlight.style.height = `${window.innerHeight}px`;
    } else {
      this._highlight.style.display = 'none';
    }

    // Dialog placement: beside the target, clamped to the viewport, and
    // never on top of the OPEN upgrade panel (tip 2 — the panel carries
    // the branch buttons the user must click) NOR the floating Quit
    // button (the only way OUT must stay visible + clickable); centered
    // for the target-less info tips. A user-dragged dialog is PINNED —
    // the tick keeps the spotlight moving but leaves the dialog where
    // the user put it.
    if (this._userPinned) return;
    const w = this._dialog.offsetWidth || 320;
    const h = this._dialog.offsetHeight || 140;
    let x: number;
    let y: number;
    const margin = 12;
    const avoidRects = this._avoidRects();
    if (!rect) {
      x = (window.innerWidth - w) / 2;
      y = (window.innerHeight - h) / 2;
    } else {
      const yAligned = Math.max(margin, Math.min(rect.top, window.innerHeight - h - margin));
      const xAligned = Math.max(margin, Math.min(rect.left, window.innerWidth - w - margin));
      const candidates: Array<[number, number]> = [
        [rect.left + rect.width + 16, yAligned], // right of the target
        [rect.left - w - 16, yAligned], // left of the target
        [xAligned, rect.top + rect.height + 16], // below the target
        [xAligned, rect.top - h - 16], // above the target
      ];
      const inViewport = (cx: number, cy: number) =>
        cx >= margin && cy >= margin
        && cx + w <= window.innerWidth - margin && cy + h <= window.innerHeight - margin;
      const clearAvoids = (cx: number, cy: number) =>
        avoidRects.every(
          (avoid) => !this._intersects({ left: cx, top: cy, width: w, height: h }, avoid)
        );
      const chosen = candidates.find(([cx, cy]) => inViewport(cx, cy) && clearAvoids(cx, cy))
        ?? candidates[0];
      x = Math.max(margin, Math.min(chosen[0], Math.max(margin, window.innerWidth - w - margin)));
      y = Math.max(margin, Math.min(chosen[1], Math.max(margin, window.innerHeight - h - margin)));
    }
    this._dialog.style.left = `${x}px`;
    this._dialog.style.top = `${y}px`;
  }

  /**
   * Rects the tip dialog must never cover: the open upgrade panel (its
   * branch buttons must stay clickable in tip 2) and the floating Quit
   * button (always visible in the upper-right — the way OUT).
   */
  private _avoidRects(): Array<{ left: number; top: number; width: number; height: number }> {
    const rects: Array<{ left: number; top: number; width: number; height: number }> = [];
    const panel = this._upgradePanelRect();
    if (panel) rects.push(panel);
    const quit = this._quitBtn?.getBoundingClientRect();
    if (quit && quit.width > 0) {
      rects.push({ left: quit.left, top: quit.top, width: quit.width, height: quit.height });
    }
    return rects;
  }

  /**
   * The open upgrade panel's viewport rect (null while it is closed) —
   * the tip dialog must never sit on top of it.
   */
  private _upgradePanelRect(): { left: number; top: number; width: number; height: number } | null {
    const panel = document.getElementById('upgrade-panel') as HTMLElement | null;
    if (!panel || !panel.classList.contains('visible')) return null;
    const rect = panel.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
  }

  private _intersects(
    a: { left: number; top: number; width: number; height: number },
    b: { left: number; top: number; width: number; height: number },
  ): boolean {
    return a.left < b.left + b.width && a.left + a.width > b.left
      && a.top < b.top + b.height && a.top + a.height > b.top;
  }

  // ─── Small utils ─────────────────────────────────────────────────────────

  private _sameOrder(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  private _sameSet(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const set = new Set(a);
    for (const id of b) {
      if (!set.has(id)) return false;
    }
    return true;
  }
}
